/**
 * MaterialRequestForm Component
 *
 * Main form for technicians to request materials.
 * Supports inventory materials (with FIFO batch selection) and external purchases.
 *
 * @module pages/technician/materials/MaterialRequestForm
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Container,
  Card,
  Form,
  Button,
  Spinner,
  Alert,
  Row,
  Col,
} from 'react-bootstrap';
import { useAuth } from '../../../contexts/AuthContext';
import { getAssignedProjects } from '../../../services/technicianProjectService';
import { createMaterialRequest, getMaterialRequests } from '../../../services/materialRequestService';
import { supabase } from '../../../services/supabase';
import { MATERIAL_REQUEST_PRIORITY } from '../../../utils/constants';
import { formatCurrency } from '../../../utils/formatters';
import MaterialSearch from './MaterialSearch';
import BatchPicker from './BatchPicker';
import MaterialRequestCard from './MaterialRequestCard';
import QuickRequestShortcut from './QuickRequestShortcut';
import './MaterialRequestForm.css';

const REQUEST_TYPE = {
  INVENTORY: 'inventory',
  EXTERNAL: 'external',
};

const MaterialRequestForm = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user, isAdmin, isSupervisor } = useAuth();

  // Internal purchase-cost gate — mirrors `canViewUnitCost` in BatchPicker.js
  // and `canViewInternalCosts` in src/pages/projects/ProjectDetail.js:99.
  // Allowlist, not `!isTechnician`: an unresolved profile must fail closed.
  const canViewInternalCost = isAdmin || isSupervisor;

  // Form state
  const [requestType, setRequestType] = useState(REQUEST_TYPE.INVENTORY);
  const [selectedProject, setSelectedProject] = useState('');
  const [selectedTask, setSelectedTask] = useState('');
  const [selectedMaterial, setSelectedMaterial] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [priority, setPriority] = useState(MATERIAL_REQUEST_PRIORITY.NORMAL);
  const [notes, setNotes] = useState('');
  const [neededBy, setNeededBy] = useState('');

  // External purchase fields
  const [externalSupplier, setExternalSupplier] = useState('');
  const [externalDescription, setExternalDescription] = useState('');
  const [externalQuantity, setExternalQuantity] = useState(1);
  const [externalUnit, setExternalUnit] = useState('unidad');
  const [externalFiles, setExternalFiles] = useState([]);

  // Batch info from BatchPicker
  const [batchInfo, setBatchInfo] = useState(null);

  // Tasks for selected project
  const [projectTasks, setProjectTasks] = useState([]);
  const [loadingTasks, setLoadingTasks] = useState(false);

  // Submission state
  const [submitError, setSubmitError] = useState(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  // Fetch assigned projects
  const { data: projectsData, isLoading: loadingProjects } = useQuery({
    queryKey: ['technician-projects', user?.id],
    queryFn: () => getAssignedProjects(user?.id),
    enabled: !!user?.id,
  });

  const projects = projectsData?.data || [];

  // Fetch recent requests
  const { data: requestsData, refetch: refetchRequests } = useQuery({
    queryKey: ['material-requests', user?.id],
    queryFn: () => getMaterialRequests(user?.id, { pageSize: 5 }),
    enabled: !!user?.id,
  });

  const recentRequests = requestsData?.data || [];

  // Fetch tasks when project is selected
  useEffect(() => {
    const fetchTasks = async () => {
      if (!selectedProject || !user?.id) {
        setProjectTasks([]);
        return;
      }

      setLoadingTasks(true);
      try {
        // Get task IDs assigned to this user via task_assignments
        const { data: assignments } = await supabase
          .from('task_assignments')
          .select('task_id')
          .eq('user_id', user.id);

        const taskIds = (assignments || []).map(a => a.task_id);

        const { data, error } = await supabase
          .from('tasks')
          .select('id, task_number, title')
          .eq('project_id', selectedProject)
          .in('id', taskIds.length > 0 ? taskIds : ['00000000-0000-0000-0000-000000000000'])
          .order('task_number', { ascending: true });

        if (error) throw error;
        setProjectTasks(data || []);
      } catch (err) {
        console.error('Fetch tasks error:', err);
        setProjectTasks([]);
      } finally {
        setLoadingTasks(false);
      }
    };

    fetchTasks();
    setSelectedTask('');
  }, [selectedProject, user?.id]);

  // Create material request mutation
  //
  // FALSE SUCCESS (fixed here): `createMaterialRequest` never throws — it
  // catches everything and returns `{ data: null, error }`. Passing it straight
  // to `mutationFn` meant the promise RESOLVED on failure, react-query called
  // `onSuccess`, and the technician got the green "Solicitud enviada
  // exitosamente" toast for a request that was never written. A technician
  // waiting on site for a material nobody ordered is the worst possible way to
  // learn about a validation error, so the error is re-thrown here and lands in
  // `onError` like any other failure.
  const createRequestMutation = useMutation({
    mutationFn: async (payload) => {
      const { data, error } = await createMaterialRequest(payload);
      if (error) throw error;
      // Belt and braces: a null row with no error would also be a silent
      // no-op. The service always returns one or the other, but "success"
      // here must mean a row exists.
      if (!data) throw new Error('No se pudo crear la solicitud');
      return data;
    },
    onSuccess: () => {
      setSubmitSuccess(true);
      setSubmitError(null);
      queryClient.invalidateQueries(['material-requests']);
      refetchRequests();

      // Reset form
      setSelectedMaterial(null);
      setQuantity(1);
      setNotes('');
      setNeededBy('');
      setBatchInfo(null);

      // Hide success message after 3 seconds
      setTimeout(() => setSubmitSuccess(false), 3000);
    },
    onError: (error) => {
      setSubmitError(error.message || 'Error al crear la solicitud');
      setSubmitSuccess(false);
    },
  });

  // Handle material selection
  const handleMaterialSelect = (material) => {
    setSelectedMaterial(material);
    setQuantity(1);
    setBatchInfo(null);
  };

  // Handle material removal
  const handleMaterialRemove = () => {
    setSelectedMaterial(null);
    setQuantity(1);
    setBatchInfo(null);
  };

  // Handle batch info update from BatchPicker
  const handleBatchInfo = useCallback((info) => {
    setBatchInfo(info);
  }, []);

  // Handle quantity change
  const handleQuantityChange = (newQuantity) => {
    const maxQuantity = batchInfo?.totalAvailable || 9999;
    const clampedQuantity = Math.max(1, Math.min(newQuantity, maxQuantity));
    setQuantity(clampedQuantity);
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError(null);

    if (requestType === REQUEST_TYPE.INVENTORY) {
      // Validate inventory request
      if (!selectedProject) {
        setSubmitError('Selecciona un proyecto');
        return;
      }

      if (!selectedMaterial) {
        setSubmitError('Selecciona un material');
        return;
      }

      if (quantity <= 0) {
        setSubmitError('La cantidad debe ser mayor a 0');
        return;
      }

      if (!batchInfo?.sufficient) {
        setSubmitError('Stock insuficiente para la cantidad solicitada');
        return;
      }

      // `unit` is NOT NULL on material_requests and the catalogue item is the
      // only place it exists. Say so instead of inventing 'unidad' (#356: an
      // absent unit means "nobody said", not "units") and instead of letting
      // the insert fail with a message written for a developer.
      if (!selectedMaterial.unit) {
        setSubmitError(
          `El material "${selectedMaterial.name}" no tiene unidad de medida registrada. ` +
          'Avisa a un administrador para completarla antes de solicitarlo.'
        );
        return;
      }

      // Submit inventory request.
      //
      // The payload keys are the service's contract, not the form's local
      // state names. This used to send `quantity` (and no `unit` at all) while
      // `createMaterialRequest` requires `quantity_requested` + `unit` — both
      // are `NOT NULL` in `material_requests` — so every submission failed
      // validation before it ever reached Supabase. `unit` comes from the
      // selected inventory item, which is the only place it exists.
      //
      // `priority` and `needed_by` are no longer sent as top-level keys:
      // neither column exists on `material_requests` (verified by SELECT on
      // information_schema.columns), and `createMaterialRequest` whitelists the
      // columns it inserts, so both were being dropped on the floor without a
      // word. The urgency and the date the technician picked now travel in
      // `notes`, which is where a reviewer will actually read them.
      const urgencyNote = priority === MATERIAL_REQUEST_PRIORITY.URGENT ? 'URGENTE' : null;
      const neededByNote = neededBy ? `Necesario para: ${neededBy}` : null;
      const composedNotes = [urgencyNote, neededByNote, notes || null]
        .filter(Boolean)
        .join(' | ') || null;

      createRequestMutation.mutate({
        project_id: selectedProject,
        task_id: selectedTask || null,
        inventory_id: selectedMaterial.id,
        quantity_requested: quantity,
        unit: selectedMaterial.unit,
        notes: composedNotes,
      });
    } else {
      // External purchase request (would need different handling)
      // For now, show a message that this feature is coming soon
      setSubmitError('Las compras externas estaran disponibles proximamente');
    }
  };

  // Handle request cancellation
  const handleRequestCancelled = () => {
    refetchRequests();
  };

  const isSubmitting = createRequestMutation.isPending;

  // Primera condicion que falta, en el orden en que el tecnico llena el
  // formulario. `canSubmit` colapsaba cuatro condiciones en un booleano, asi
  // que quien escribia una cantidad mayor al stock veia un boton gris y ninguna
  // explicacion. Los textos son los mismos que ya usa `handleSubmit`, para que
  // el aviso previo y el error posterior no se contradigan.
  const blockingReason = (() => {
    if (!selectedProject) return 'Elegi el proyecto para el que pedis el material.';
    if (requestType === REQUEST_TYPE.INVENTORY) {
      if (!selectedMaterial) return 'Elegi el material del inventario.';
      if (!(quantity > 0)) return 'La cantidad tiene que ser mayor a 0.';
      if (!batchInfo?.sufficient) {
        return 'No hay stock suficiente para esa cantidad. Baja la cantidad o pedila como compra externa.';
      }
      return null;
    }
    if (!externalDescription) return 'Escribi que material necesitas.';
    if (!(externalQuantity > 0)) return 'La cantidad tiene que ser mayor a 0.';
    return null;
  })();

  const canSubmit = blockingReason === null;

  return (
    <Container fluid className="material-request-page">
      {/* Header */}
      <div className="material-request-header">
        <h1 className="page-title">Solicitar Materiales</h1>
        <p className="page-subtitle">
          Solicita materiales del inventario o compras externas
        </p>
      </div>

      {/* Camino rapido. Reemplaza al aviso que habia aca, que era un `Alert`
          con un boton `size="sm"` (~31px de alto, bajo el piso de 44px) rotulado
          con el nombre interno de la pantalla ("Solicitud inteligente") y que
          hablaba de "la IA". Mismo componente que en /tecnico/materiales para
          que el atajo se vea igual en los dos lados. El formulario sigue
          completo y visible debajo. */}
      <QuickRequestShortcut />

      {/* Success Alert */}
      {submitSuccess && (
        <Alert variant="success" className="mb-3">
          <i className="bi bi-check-circle me-2"></i>
          Solicitud enviada exitosamente. Un administrador la revisara pronto.
        </Alert>
      )}

      {/* Error Alert */}
      {submitError && (
        <Alert variant="danger" className="mb-3" dismissible onClose={() => setSubmitError(null)}>
          <i className="bi bi-exclamation-circle me-2"></i>
          {submitError}
        </Alert>
      )}

      {/* Main Form Card */}
      <Card className="material-form-card mb-4">
        <Card.Body>
          <Form onSubmit={handleSubmit}>
            {/* Request Type Toggle */}
            <div className="form-section">
              <div className="form-section-title">Tipo de Solicitud</div>
              <div className="request-type-toggle">
                <button
                  type="button"
                  className={`request-type-btn ${
                    requestType === REQUEST_TYPE.INVENTORY ? 'active' : ''
                  }`}
                  onClick={() => setRequestType(REQUEST_TYPE.INVENTORY)}
                >
                  <i className="bi bi-box-seam"></i>
                  <span>Del Inventario</span>
                </button>
                <button
                  type="button"
                  className={`request-type-btn ${
                    requestType === REQUEST_TYPE.EXTERNAL ? 'active' : ''
                  }`}
                  onClick={() => setRequestType(REQUEST_TYPE.EXTERNAL)}
                >
                  <i className="bi bi-cart-plus"></i>
                  <span>Compra Externa</span>
                </button>
              </div>
            </div>

            {/* Project Selection */}
            <div className="form-section">
              <Form.Group className="mb-3">
                <Form.Label>
                  Proyecto <span className="required">*</span>
                </Form.Label>
                {loadingProjects ? (
                  <div className="d-flex align-items-center gap-2">
                    <Spinner animation="border" size="sm" />
                    <span className="text-muted">Cargando proyectos...</span>
                  </div>
                ) : projects.length > 0 ? (
                  <Form.Select
                    value={selectedProject}
                    onChange={(e) => setSelectedProject(e.target.value)}
                    required
                  >
                    <option value="">Seleccionar proyecto...</option>
                    {projects.map((project) => (
                      <option key={project.id} value={project.id}>
                        {project.project_number} - {project.title}
                      </option>
                    ))}
                  </Form.Select>
                ) : (
                  <Alert variant="warning" className="mb-0">
                    No tienes proyectos asignados actualmente.
                  </Alert>
                )}
              </Form.Group>

              {/* Task Selection (Optional) */}
              {selectedProject && (
                <Form.Group className="mb-3">
                  <Form.Label>Tarea (Opcional)</Form.Label>
                  {loadingTasks ? (
                    <div className="d-flex align-items-center gap-2">
                      <Spinner animation="border" size="sm" />
                      <span className="text-muted">Cargando tareas...</span>
                    </div>
                  ) : projectTasks.length > 0 ? (
                    <Form.Select
                      value={selectedTask}
                      onChange={(e) => setSelectedTask(e.target.value)}
                    >
                      <option value="">Sin tarea especifica</option>
                      {projectTasks.map((task) => (
                        <option key={task.id} value={task.id}>
                          {task.task_number} - {task.title}
                        </option>
                      ))}
                    </Form.Select>
                  ) : (
                    <p className="text-muted small mb-0">
                      No tienes tareas asignadas en este proyecto
                    </p>
                  )}
                </Form.Group>
              )}
            </div>

            {/* Inventory Material Selection */}
            {requestType === REQUEST_TYPE.INVENTORY && (
              <div className="form-section">
                <Form.Group className="mb-3">
                  <Form.Label>
                    Material <span className="required">*</span>
                  </Form.Label>

                  {/* Material Search */}
                  <MaterialSearch
                    onSelect={handleMaterialSelect}
                    selectedMaterial={selectedMaterial}
                    disabled={!selectedProject}
                  />

                  {/* Selected Material Card */}
                  {selectedMaterial && (
                    <div className="selected-material-card">
                      <div className="selected-material-header">
                        <div className="selected-material-info">
                          <div className="selected-material-name">
                            {selectedMaterial.name}
                          </div>
                          {selectedMaterial.sku && (
                            <div className="selected-material-sku">
                              {selectedMaterial.sku}
                            </div>
                          )}
                        </div>
                        <button
                          type="button"
                          className="selected-material-remove"
                          onClick={handleMaterialRemove}
                          aria-label="Remover material"
                        >
                          <i className="bi bi-x-lg"></i>
                        </button>
                      </div>
                      <div className="selected-material-stock">
                        <div className="selected-material-stock-item">
                          <span className="selected-material-stock-label">
                            Stock Disponible
                          </span>
                          <span className="selected-material-stock-value">
                            {selectedMaterial.current_stock}{' '}
                            {selectedMaterial.unit}
                          </span>
                        </div>
                        <div className="selected-material-stock-item">
                          <span className="selected-material-stock-label">
                            Minimo
                          </span>
                          <span className="selected-material-stock-value">
                            {selectedMaterial.min_stock_level}{' '}
                            {selectedMaterial.unit}
                          </span>
                        </div>
                      </div>

                      {/* Batch Picker */}
                      <BatchPicker
                        inventoryId={selectedMaterial.id}
                        requestedQuantity={quantity}
                        onBatchInfo={handleBatchInfo}
                        unit={selectedMaterial.unit}
                      />

                      {/* Quantity Input */}
                      <div className="quantity-input-container">
                        <Form.Label>
                          Cantidad Solicitada <span className="required">*</span>
                        </Form.Label>
                        <div className="quantity-input-wrapper">
                          <button
                            type="button"
                            className="quantity-control"
                            onClick={() => handleQuantityChange(quantity - 1)}
                            disabled={quantity <= 1}
                          >
                            <i className="bi bi-dash"></i>
                          </button>
                          <Form.Control
                            type="number"
                            className="quantity-input"
                            value={quantity}
                            onChange={(e) =>
                              handleQuantityChange(parseInt(e.target.value) || 1)
                            }
                            min={1}
                            max={batchInfo?.totalAvailable || 9999}
                            required
                          />
                          <button
                            type="button"
                            className="quantity-control"
                            onClick={() => handleQuantityChange(quantity + 1)}
                            disabled={
                              batchInfo?.totalAvailable &&
                              quantity >= batchInfo.totalAvailable
                            }
                          >
                            <i className="bi bi-plus"></i>
                          </button>
                        </div>
                        {batchInfo && (
                          <div className="quantity-max-label">
                            Maximo disponible: {batchInfo.totalAvailable}{' '}
                            {selectedMaterial.unit}
                          </div>
                        )}
                      </div>

                      {/*
                        Estimated Cost — internal purchase cost, admin/supervisor only.
                        `batchInfo.estimatedCost` comes from `inventory_batches.unit_cost`
                        (what ProMedic PAID). BatchPicker now refuses to emit it at all for
                        anyone outside the allowlist (`canViewUnitCost`), so for a technician
                        this is `null` and the block never renders. The explicit
                        `canViewInternalCost` check below is the second half of the same
                        defence: the source stops producing it AND the surface stops printing
                        it, so neither one alone silently re-opens the leak.
                      */}
                      {canViewInternalCost && batchInfo?.sufficient && batchInfo.estimatedCost > 0 && (
                        <div
                          style={{
                            marginTop: '12px',
                            padding: '12px',
                            background: '#f5f7fa',
                            borderRadius: '8px',
                            textAlign: 'center',
                          }}
                        >
                          <span className="text-muted">Costo Estimado: </span>
                          <strong>{formatCurrency(batchInfo.estimatedCost)}</strong>
                        </div>
                      )}
                    </div>
                  )}
                </Form.Group>
              </div>
            )}

            {/* External Purchase Fields */}
            {requestType === REQUEST_TYPE.EXTERNAL && (
              <div className="form-section external-purchase-fields">
                <div className="external-purchase-note">
                  <i className="bi bi-info-circle"></i>
                  <span>
                    Las compras externas requieren aprobacion adicional y pueden
                    tomar mas tiempo.
                  </span>
                </div>

                <Form.Group className="mb-3">
                  <Form.Label>
                    Descripcion del Material <span className="required">*</span>
                  </Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    placeholder="Describe el material que necesitas..."
                    value={externalDescription}
                    onChange={(e) => setExternalDescription(e.target.value)}
                    required={requestType === REQUEST_TYPE.EXTERNAL}
                  />
                </Form.Group>

                <Row>
                  <Col xs={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Cantidad</Form.Label>
                      <Form.Control
                        type="number"
                        value={externalQuantity}
                        onChange={(e) =>
                          setExternalQuantity(parseInt(e.target.value) || 1)
                        }
                        min={1}
                        required={requestType === REQUEST_TYPE.EXTERNAL}
                      />
                    </Form.Group>
                  </Col>
                  <Col xs={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Unidad</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="ej: metros, kg"
                        value={externalUnit}
                        onChange={(e) => setExternalUnit(e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Form.Group className="mb-3">
                  <Form.Label>Proveedor Sugerido (Opcional)</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Nombre del proveedor..."
                    value={externalSupplier}
                    onChange={(e) => setExternalSupplier(e.target.value)}
                  />
                </Form.Group>

                {/* File Attachment for quotation/invoice */}
                <Form.Group className="mb-3">
                  <Form.Label>Cotizacion / Factura (Opcional)</Form.Label>
                  <div className="file-attachment-area">
                    <label className="file-attachment-btn" htmlFor="external-file-input">
                      <i className="bi bi-paperclip me-2"></i>
                      {externalFiles.length > 0
                        ? `${externalFiles.length} archivo(s) adjunto(s)`
                        : 'Adjuntar cotizacion o factura'}
                    </label>
                    <input
                      id="external-file-input"
                      type="file"
                      className="d-none"
                      accept=".pdf,.jpg,.jpeg,.png,.webp"
                      multiple
                      onChange={(e) => {
                        const files = Array.from(e.target.files || []);
                        setExternalFiles(prev => [...prev, ...files]);
                        e.target.value = '';
                      }}
                    />
                    {externalFiles.length > 0 && (
                      <div className="file-attachment-list">
                        {externalFiles.map((file, idx) => (
                          <div key={idx} className="file-attachment-item">
                            <i className={`bi ${file.type.includes('pdf') ? 'bi-file-earmark-pdf text-danger' : 'bi-file-earmark-image text-primary'} me-2`}></i>
                            <span className="file-attachment-name">{file.name}</span>
                            <button
                              type="button"
                              className="file-attachment-remove"
                              onClick={() => setExternalFiles(prev => prev.filter((_, i) => i !== idx))}
                              aria-label="Remover archivo"
                            >
                              <i className="bi bi-x"></i>
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <Form.Text className="text-muted">
                    PDF, JPG o PNG. Maximo 5MB por archivo.
                  </Form.Text>
                </Form.Group>
              </div>
            )}

            {/* Priority */}
            <div className="form-section">
              <Form.Label>Prioridad</Form.Label>
              <div className="priority-toggle">
                <button
                  type="button"
                  className={`priority-btn normal ${
                    priority === MATERIAL_REQUEST_PRIORITY.NORMAL ? 'active' : ''
                  }`}
                  onClick={() => setPriority(MATERIAL_REQUEST_PRIORITY.NORMAL)}
                >
                  <i className="bi bi-circle me-1"></i>
                  Normal
                </button>
                <button
                  type="button"
                  className={`priority-btn urgent ${
                    priority === MATERIAL_REQUEST_PRIORITY.URGENT ? 'active' : ''
                  }`}
                  onClick={() => setPriority(MATERIAL_REQUEST_PRIORITY.URGENT)}
                >
                  <i className="bi bi-exclamation-triangle me-1"></i>
                  Urgente
                </button>
              </div>
            </div>

            {/* Additional Options */}
            <div className="form-section">
              <Form.Group className="mb-3">
                <Form.Label>Fecha Necesaria (Opcional)</Form.Label>
                <Form.Control
                  type="date"
                  value={neededBy}
                  onChange={(e) => setNeededBy(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Notas (Opcional)</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  placeholder="Informacion adicional sobre la solicitud..."
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  maxLength={500}
                />
                <Form.Text className="text-muted">
                  {notes.length}/500 caracteres
                </Form.Text>
              </Form.Group>
            </div>

            {/* Submit Button */}
            <div className="submit-button-container">
              <Button
                type="submit"
                variant="primary"
                className="submit-btn"
                disabled={!canSubmit || isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Enviando Solicitud...
                  </>
                ) : (
                  <>
                    <i className="bi bi-send me-2"></i>
                    Enviar Solicitud
                  </>
                )}
              </Button>
              {blockingReason && !isSubmitting && (
                <p className="submit-blocked-reason" role="status">
                  <i className="bi bi-info-circle me-1" aria-hidden="true"></i>
                  {blockingReason}
                </p>
              )}
            </div>
          </Form>
        </Card.Body>
      </Card>

      {/* Recent Requests */}
      {recentRequests.length > 0 && (
        <div className="recent-requests-section">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h6 className="mb-0">Solicitudes Recientes</h6>
            <Button
              variant="link"
              size="sm"
              onClick={() => navigate('/tecnico/materiales/historial')}
            >
              Ver Todas
            </Button>
          </div>
          {recentRequests.map((request) => (
            <MaterialRequestCard
              key={request.id}
              request={request}
              onCancelled={handleRequestCancelled}
            />
          ))}
        </div>
      )}
    </Container>
  );
};

export default MaterialRequestForm;
